import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "className", "tooltip", "tooltipColor", "onTooltipEntered", "tooltipHideOnClick", "tooltipIcon", "tooltipPosition", "tooltipShowOnClick", "tooltipSize", "tooltipShowDelay", "zIndex", "onClick", "onMouseEnter", "onMouseLeave", "tooltipActive", "ComposedComponent"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as RadixTooltip from '@radix-ui/react-tooltip';
import cx from 'classnames';
import omit from 'lodash.omit';
import React, { useEffect, useRef, useState } from 'react';
import uiUtilities from '../../foundation/utilities/index.css';
import Box from '../box';
import theme from './theme.css';
export var POSITIONS = {
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top'
};
var SIZE_MAP = {
  large: {
    padding: 4
  },
  medium: {
    padding: 3
  },
  small: {
    paddingHorizontal: 3,
    paddingVertical: 2
  }
};
var Arrow = function Arrow() {
  return /*#__PURE__*/React.createElement("div", {
    className: theme['arrow']
  });
};
var TooltippedComponent = function TooltippedComponent(_ref) {
  var children = _ref.children,
    className = _ref.className,
    tooltip = _ref.tooltip,
    _ref$tooltipColor = _ref.tooltipColor,
    tooltipColor = _ref$tooltipColor === void 0 ? 'white' : _ref$tooltipColor,
    onTooltipEntered = _ref.onTooltipEntered,
    _ref$tooltipHideOnCli = _ref.tooltipHideOnClick,
    tooltipHideOnClick = _ref$tooltipHideOnCli === void 0 ? true : _ref$tooltipHideOnCli,
    _ref$tooltipIcon = _ref.tooltipIcon,
    tooltipIcon = _ref$tooltipIcon === void 0 ? null : _ref$tooltipIcon,
    _ref$tooltipPosition = _ref.tooltipPosition,
    tooltipPosition = _ref$tooltipPosition === void 0 ? 'top' : _ref$tooltipPosition,
    _ref$tooltipShowOnCli = _ref.tooltipShowOnClick,
    tooltipShowOnClick = _ref$tooltipShowOnCli === void 0 ? false : _ref$tooltipShowOnCli,
    _ref$tooltipSize = _ref.tooltipSize,
    tooltipSize = _ref$tooltipSize === void 0 ? 'medium' : _ref$tooltipSize,
    _ref$tooltipShowDelay = _ref.tooltipShowDelay,
    tooltipShowDelay = _ref$tooltipShowDelay === void 0 ? 100 : _ref$tooltipShowDelay,
    _ref$zIndex = _ref.zIndex,
    zIndex = _ref$zIndex === void 0 ? 700 : _ref$zIndex,
    onClick = _ref.onClick,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    tooltipActive = _ref.tooltipActive,
    ComposedComponent = _ref.ComposedComponent,
    other = _objectWithoutProperties(_ref, _excluded);
  var ref = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    active = _useState2[0],
    setActive = _useState2[1];
  var timeoutRef = useRef(null);
  var handleMouseEnter = function handleMouseEnter(event) {
    timeoutRef.current = setTimeout(function () {
      setActive(true);
      if (onMouseEnter) {
        onMouseEnter(event);
      }
    }, tooltipShowDelay);
  };
  var handleMouseLeave = function handleMouseLeave(event) {
    clearTimeout(timeoutRef.current);
    setActive(false);
    if (onMouseLeave) {
      onMouseLeave(event);
    }
  };
  useEffect(function () {
    return function () {
      clearTimeout(timeoutRef.current);
    };
  }, [timeoutRef]);
  var handleClick = function handleClick(event) {
    if (tooltipHideOnClick && active) {
      setActive(false);
    }
    if (tooltipShowOnClick && !active) {
      setActive(true);
    }
    if (onClick) {
      onClick(event);
    }
  };
  var handleOpenChange = function handleOpenChange(open) {
    if (open && onTooltipEntered) {
      onTooltipEntered();
    }
  };
  useEffect(function () {
    if (tooltipActive && !active) {
      setActive(true);
    }
    if (!tooltipActive && active) {
      setActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltipActive]);
  var rest = omit(other, ['onTooltipEntered', 'tooltipHideOnClick', 'tooltipPosition', 'tooltipShowOnClick', 'tooltipShowDelay']);
  var childProps = _objectSpread(_objectSpread({}, rest), {}, {
    className: className,
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    ref: ref
  });
  if (tooltipActive === undefined) {
    childProps = _objectSpread(_objectSpread({}, childProps), {}, {
      onClick: handleClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    });
  }

  // Using the radix tooltip component, but we only use it for rendering the tooltip
  // we still manually implement the trigger with mouseover/leave/click and keep that in state.
  // With a pure radix implementation we couldn't support our `tooltipHideOnClick` prop.
  return /*#__PURE__*/React.createElement(RadixTooltip.Provider, {
    delayDuration: tooltipShowDelay
  }, /*#__PURE__*/React.createElement(RadixTooltip.Root, {
    open: active,
    onOpenChange: handleOpenChange
  }, /*#__PURE__*/React.createElement(RadixTooltip.Trigger, {
    asChild: true
  }, /*#__PURE__*/React.createElement(ComposedComponent, childProps, children)), /*#__PURE__*/React.createElement(RadixTooltip.Portal, {
    forceMount: active || undefined
  }, /*#__PURE__*/React.createElement(RadixTooltip.Content, {
    className: cx(uiUtilities['box-shadow-200'], theme['tooltip-content'], theme[tooltipColor], theme[tooltipSize]),
    sideOffset: 8,
    side: tooltipPosition,
    style: {
      zIndex: zIndex
    },
    "data-teamleader-ui": "tooltip"
  }, /*#__PURE__*/React.createElement(Box, _extends({
    className: theme['inner']
  }, SIZE_MAP[tooltipSize]), tooltipIcon && /*#__PURE__*/React.createElement("div", {
    className: theme['icon']
  }, tooltipIcon), /*#__PURE__*/React.createElement("div", {
    className: theme['text']
  }, tooltip)), /*#__PURE__*/React.createElement(RadixTooltip.Arrow, {
    asChild: true
  }, /*#__PURE__*/React.createElement(Arrow, null))))));
};
function Tooltip(ComposedComponent) {
  var WrappedComponent = function WrappedComponent(props) {
    return /*#__PURE__*/React.createElement(TooltippedComponent, _extends({}, props, {
      tooltip: props.tooltip,
      ComposedComponent: ComposedComponent
    }), props.children);
  };
  return WrappedComponent;
}
export default Tooltip;